import { useAppSelector } from 'hooks/useAppSelector';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAssetTypes } from 'selectors';
import get from 'lodash/get';
import { format } from 'erisxkit/client';
import insertArrayAt from 'utils/insertArrayAt';
import { columnDefs, TOTAL_USD_VALUE_COL_ID } from '../columnDefs';
import createAssetTypeColDef from './createAssetTypeColDef';
import BalanceGridRow, { AssetType } from '../types';
import { getTotalUSDValues } from '../utils';
import { ColDef, GridState, StateUpdatedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { roles } from 'constants/userRoles';

export const BALANCES_READ_ONLY_ROLES = [roles.RISK];
type UseBalancesColDefsArgs = {
  initialState?: GridState;
  gridRef?: React.RefObject<AgGridReact>;
};
const useBalancesColDefs = ({
  gridRef,
  initialState,
}: UseBalancesColDefsArgs) => {
  const assetTypes: AssetType[] = useAppSelector(getAssetTypes);
  const prevAssetTypes = useRef<AssetType[] | null>(null);

  useEffect(() => {
    if (prevAssetTypes.current !== null) {
      const api = gridRef?.current?.api;

      if (api && initialState) {
        api.setState(initialState);
      }
    }
  }, [initialState, gridRef, assetTypes]);

  useEffect(() => {
    if (prevAssetTypes?.current === null) {
      prevAssetTypes.current = assetTypes;
    }
  }, [assetTypes, gridRef, initialState]);

  const balanceOnlyColDefs: ColDef<BalanceGridRow>[] = useMemo(() => {
    return [
      {
        headerName: 'Reg Type',
        field: 'fundsDesignation',
        filter: 'agTextColumnFilter',
        initialWidth: 120,
      },
      {
        headerName: 'Total USD Value',
        colId: TOTAL_USD_VALUE_COL_ID,
        valueFormatter: ({ data }: { data?: BalanceGridRow }) =>
          `$${format(getTotalUSDValues(get(data, ['balancesUSD'], [])), {})}`,
        sortable: false,
        initialWidth: 170,
      },
      ...assetTypes.map(createAssetTypeColDef),
    ];
  }, [assetTypes]);

  const colDefs = useMemo(() => {
    const index = columnDefs.findIndex((colDef) => colDef.field === 'name');
    return insertArrayAt(columnDefs, balanceOnlyColDefs, index);
  }, [balanceOnlyColDefs]);

  return colDefs;
};

export default useBalancesColDefs;
